import config from "react-global-configuration"
import { call, put, select, takeLatest } from "redux-saga/effects"

import opticActions, { CheckOpticPathPayload } from "client/optic/actions"
import { CALL_HAS_CHANGED } from "containers/Call/services/constants"
import { CALL_FINISHED } from "containers/Call/services/saga"
import surveyActions from "containers/Survey/services/actions"
import { Question, QUESTION_TYPE } from "containers/Survey/types"
import { languages } from "lib/languages"
import { request } from "lib/request"
import { PATH } from "models/survey/_paths"
import { FOR_PATIENT } from "models/survey/constants"
import { DoctorType, MedicalPath } from "types/entity"
import { Call, CallState, ResponseMedicalPath } from "types/payload"
import { Action, GFlow, Message } from "types/redux"

import { CHECK_OPTIC_PATH } from "./constants"
import { first } from "lodash"
import { OPTIC_STEPS } from "models/survey/_survey"
import { push } from "redux-first-history"
import { PAYMENT_PAGE, SURVEY_PAGE } from "core/constants"

async function createMedicalPathApi(): Promise<ResponseMedicalPath> {
  return await request(`${config.get("optic.get.medicalPath")}`, {
    method: "POST",
  })
}

export async function getMedicalPathApi(): Promise<ResponseMedicalPath> {
  return await request(`${config.get("optic.get.medicalPath")}`, {
    method: "GET",
  })
}

export const getNextDoctor = (path?: MedicalPath): DoctorType | false => {
  if (!path) return false
  else if (path.is_orthoptia && path.is_opthalmology) return false
  else if (path.is_orthoptia) return DoctorType.OPHTALMOLOGIST
  else return DoctorType.ORTHOPTIST
}

export function* getMedicalPath(): GFlow<MedicalPath | void> {
  const { path } = yield call(getMedicalPathApi)
  switch (path?.state) {
    case undefined:
    case null:
    case "terminated":
    case "canceled":
      const { path: newPath } = yield call(createMedicalPathApi)
      yield call(retrieveOrSetExemption, newPath)
      yield put(opticActions.setPath(newPath))
      return newPath
    case "created":
    case "ongoing":
    default:
      yield call(retrieveOrSetExemption, path)
      yield put(opticActions.setPath(path))
      return path
  }
}

function* informPatientOfMedicalPath(
  path: MedicalPath
): GFlow<Action<Message>> {
  if (!path)
    yield put(
      opticActions.inform({
        type: "success",
        text: "Aucun Parcours optique n'a été trouvé",
      })
    )
  else if (path.state === "ongoing") {
    const nextDoctorCategory = getNextDoctor(path)
    yield put(
      opticActions.inform({
        type: "success",
        text: `Nous avons trouvé un parcours optique pour vous ! prochaine mise en relation avec : ${languages.doctorType(
          nextDoctorCategory
        )}`,
      })
    )
  } else if (path.state === "created")
    yield put(
      opticActions.inform({
        type: "success",
        text: "Nous avons créé un parcours optique ! ",
      })
    )
}
function* setCurrentPatientFromPath(
  path: MedicalPath
): GFlow<Action<Question>> {
  if (path.relative_id) {
    // the medical_path is owned by a relative
    // lets push it into the survey
    yield put(
      surveyActions.setStepReplies({
        replies: [
          {
            title: "force-relative-reply-from-medicalpath",
            value: path.relative_id,
          },
        ],
        id: PATH.patient,
      })
    )
  } else {
    yield put(
      surveyActions.setStepReplies({
        replies: [
          { title: "force-patient-reply-from-medicalpath", value: FOR_PATIENT },
        ],
        id: PATH.patient,
      })
    )
  }
}
function* setExemptionFromPath(path: MedicalPath): GFlow<Action<Message>> {
  // Force exemption
  // -> @TOCHANGE Consultation Free
  yield put(
    surveyActions.setStepReplies({
      replies: [
        { title: "force-exemption-reply-from-medicalpath", value: true },
      ],
      id: PATH.exemption,
    })
  )
}

function* retrieveOrSetExemption(path: MedicalPath) {
  const nextDoctorCategory = getNextDoctor(path);
  const storagedExemption = JSON.parse(localStorage.getItem("exemption") || '{}');
  const { exemption, pathId } = storagedExemption;

  // Supprimer l'exemption en cache si elle appartient à un autre chemin.
  if (pathId && pathId !== path.id) {
    localStorage.removeItem("exemption");
  }

  if (nextDoctorCategory === DoctorType.ORTHOPTIST) {
    // Récupérer la réponse d'exemption pour un orthoptiste et la stocker si elle existe.
    const exemptionReply = yield select(({ survey }) =>
      survey.answers.find((a) => a.questionId === PATH.exemption)
    );

    if (exemptionReply) {
      const formattedExemption = [{
        ...exemptionReply,
        value: exemptionReply.value?.length ? first(exemptionReply.value) : false
      }];
      localStorage.setItem("exemption", JSON.stringify({ exemption: formattedExemption, pathId: path.id }));
    }
  } else if (nextDoctorCategory === DoctorType.OPHTALMOLOGIST && exemption && pathId === path.id) {
    // Restaurer la réponse d'exemption pour l'ophtalmologiste si le chemin est le même.
    yield put(surveyActions.setStepReplies({ replies: exemption, id: PATH.exemption }));
  }
}

function* checkPathFlow(
  action: Action<CheckOpticPathPayload | undefined>
): GFlow<any> {
  // Only reset the patient to the start of the survey when explicitly asked
  // (e.g. "Lancer une TLO" or end of a call). Background checks triggered by
  // SET_CUSTOMER (login, refresh, profile update) must not move the patient.
  // An ongoing optic path with the ophthalmologist pending always redirects
  // to payment, whatever the trigger.
  const redirect = action.payload?.redirect === true
  try {
    const path = yield call(getMedicalPath)
    yield call(informPatientOfMedicalPath, path)
    // line below is commented because optic TLC is not free
    // yield setExemptionFromPath(path)
    const nextDoctorCategory = getNextDoctor(path)
    if (nextDoctorCategory === DoctorType.OPHTALMOLOGIST) {
      // Path is ongoing and the patient owner must be set
      yield setCurrentPatientFromPath(path)
      yield put(surveyActions.setCurrentStep(PATH.payment))
      yield put(push(PAYMENT_PAGE));
    } else if (redirect) {
      const answers = yield select(({ survey }) => survey.answers);
      // s'il n'y a pas de réponse dans le store et pas de call en cours => reset du parcours
      if (!answers || answers.length === 0) yield put(push(SURVEY_PAGE));
    }
  } catch (e) {
    const error = typeof e === "string" ? e : languages.genericAPIError
    yield put(opticActions.inform({ type: "error", text: error }))
    console.error(error, {
      route: config.get("optic.get.medicalPath"),
    })
  }
}

function* checkOpticCallFLow(action: Action<Call>) {
  const call: Call = action.payload
  if (call && CALL_FINISHED.includes(call.state) && call.state !== CallState.CANCELLED_BY_DOCTOR) {
    yield put(opticActions.checkPath(true))
  }
}

function* opticWatcher() {
  yield takeLatest(CHECK_OPTIC_PATH, checkPathFlow)
  yield takeLatest(CALL_HAS_CHANGED, checkOpticCallFLow)
}
export default opticWatcher
